import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../i18n/I18nContext';
import { consoleLinks } from '../config/domains';

export default function ForAdvisors() {
  const { t, raw } = useI18n();
  const home = raw('home');

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-16">
      <section className="grid gap-10 md:grid-cols-2 md:items-center md:gap-14">
        <div>
          <p className="landing-eyebrow">{home.eyebrow}</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">{home.title}</h1>
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-slate-600">{home.subtitle}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/apply"
              className="inline-block rounded-full bg-coverfy-accent px-5 py-2.5 text-sm font-medium text-white active:scale-[0.98]"
            >
              {home.primaryCta}
            </Link>
            <Link
              to="/pricing"
              className="inline-block rounded-full border border-coverfy-accent px-5 py-2.5 text-sm font-medium text-coverfy-accent active:scale-[0.98]"
            >
              {home.secondaryCta}
            </Link>
            <a
              href={consoleLinks.login}
              className="inline-block rounded-full border border-slate-800 bg-slate-900 px-5 py-2.5 text-sm font-medium text-white active:scale-[0.98]"
              rel="noopener noreferrer"
            >
              {home.thirdCta}
            </a>
          </div>

          <p className="mt-6 text-xs text-slate-500">{home.trust}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bezel-outer p-3"
        >
          <div className="bezel-inner p-6 md:p-8">
            <div className="grid grid-cols-3 gap-3">
              {home.pillars.map((p) => (
                <div key={p.label} className="rounded-2xl border border-slate-200/60 bg-white p-4">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-coverfy-accent">{p.label}</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">{p.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2">
              {home.capabilities.map((c) => (
                <p key={c} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-coverfy-accent" />
                  <span>{c}</span>
                </p>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* 数据 stats 条 */}
      <section className="landing-section-gap grid grid-cols-2 gap-3 md:grid-cols-4">
        {home.stats.map((s) => (
          <div key={s.value} className="bezel-outer p-3">
            <div className="bezel-inner p-4 text-center">
              <p className="text-2xl font-semibold tracking-tight text-coverfy-accent md:text-3xl">{s.value}</p>
              <p className="mt-1 text-xs leading-snug text-slate-500">{s.label}</p>
            </div>
          </div>
        ))}
      </section>

      {/* AI 功能大区块（aifollowup/autoreach 匿名的智能跟进/触达） */}
      <section className="landing-section-gap">
        <div className="text-center">
          <p className="landing-eyebrow">{home.aiSection.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-2xl text-3xl font-semibold tracking-tight md:text-4xl">
            {home.aiSection.title}
          </h2>
          <p className="mx-auto mt-3 max-w-[52ch] text-sm leading-relaxed text-slate-600 md:text-base">
            {home.aiSection.subtitle}
          </p>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {home.aiSection.features.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bezel-outer p-3"
            >
              <div className="bezel-inner h-full p-6">
                <span className="inline-block rounded-full bg-coverfy-accent/10 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-widest text-coverfy-accent">
                  {f.tag}
                </span>
                <h3 className="mt-3 text-lg font-semibold tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="landing-section-gap grid gap-4 md:grid-cols-3">
        {home.manifesto.map((m, i) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            className="bezel-outer p-3"
          >
            <div className="bezel-inner p-6">
              <h3 className="text-lg font-semibold tracking-tight">{m.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.body}</p>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="landing-section-gap">
        <p className="landing-eyebrow">{t('pricing.eyebrow')}</p>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight md:text-3xl">{t('pricing.title')}</h2>
        <p className="mt-2 max-w-[48ch] text-sm text-slate-600">{t('pricing.subtitle')}</p>
        <div className="mt-6">
          <Link
            to="/pricing"
            className="inline-block rounded-full border border-coverfy-ink px-5 py-2.5 text-sm font-medium text-coverfy-ink active:scale-[0.98]"
          >
            {t('common.learnMore')} →
          </Link>
        </div>
      </section>
    </div>
  );
}
